import * as d3 from 'd3';
import readMore from '../../../../static/assets/read_more.svg';
import readMoreDark from '../../../../static/assets/read_more_dark.svg';
import { DARK, PROGRAM } from '../../../../static/keyWords.js';
import { totalCostFormat } from '../../../reusable/reusableFunctions.js';

// pixels
const barMargin = 50;
const barHeight = 30;
const subBarMargin = 35;
const centerTextPercent = 0.7;
const centerText = barHeight * centerTextPercent;
const indentation = 40;
const translateDuration = 200;
const appearDuration = 500;
const barsDuration = 500;
const iconSize = 20;
const opacityValue = 0.8;
const SEE_MORE_CHUNK_SIZE = 100;
const textFontSize = 18;
const barHeightDecreaseStep = 2;
const fontSizeDecreaseStep = 1.5;

// bar colors
const green = '#50A167';
const red = '#FF4D4D';
const blue = '#2E97F3';
const gray = '#999';

const TOGGLE_MODE = Object.freeze({
  ALLOCATIONS: 'allocations',
  PROGRAM_COSTS: 'programcosts'
});

const TOGGLE = {
  [TOGGLE_MODE.ALLOCATIONS]: TOGGLE_MODE.PROGRAM_COSTS,
  [TOGGLE_MODE.PROGRAM_COSTS]: TOGGLE_MODE.ALLOCATIONS
};

const SORT_COLUMN_BY_TOGGLE_MODE = Object.freeze({
  [TOGGLE_MODE.ALLOCATIONS]: ['percent', 'ASC'],
  [TOGGLE_MODE.PROGRAM_COSTS]: ['cost', 'DESC']
});

const getLineEnd = (node) => {
  let line = d3.select(node).select('.verticalLine');
  let transition = d3.active(line.node());
  let lineEnd;

  // if the line is still in transition, get the expected final value
  if (transition) {
    let y2Tween = transition.attrTween('y2');

    // function that returns value based on the frame of the transition
    // accepts values in the interval [0,1] - 0 - start of transition, 1 - end of transition
    let interpolator = y2Tween.call(line.node());

    // get the expected final value without waiting for the transition to be completed
    lineEnd = interpolator(1);
  } else {
    // transition has completed and the value is not changing anymore
    lineEnd = line.attr('y2');
  }

  return lineEnd;
};

const updateVerticalLines = (data, node, isExpanding, translateValue) => {
  // reduce line length when collapsing bars
  if (!isExpanding) translateValue *= -1;

  let currentLineEnd = getLineEnd(node);

  let newLineEnd = Number(currentLineEnd) + translateValue;
  d3.select(node)
    .select('.verticalLine')
    .transition()
    .duration(appearDuration)
    .attr('y2', newLineEnd);
};

const getPercentString = (allocationPercent) => {
  return (allocationPercent).toFixed(2) + '%';
};

const getPercent = (value, totalCost) => {
  if (totalCost <= 0) return '100.00';
  
  return ((value / totalCost) * 100).toFixed(2);
};

const adjustTooltipPositionToFitViewport = (d3TooltipReference, offsetTop, offsetLeft) => {
  // getBoundingClientRect gives us values based off of the viewport
  let chartViewportBoundingBox = document.querySelector('.chartContainer').getBoundingClientRect();
  let tooltipBoundingBox = d3TooltipReference.node().getBoundingClientRect();

  // get overflow values based off of the parent element
  let tooltipRightOverflow = chartViewportBoundingBox.right - tooltipBoundingBox.right;
  let tooltipBottomOverflow = chartViewportBoundingBox.bottom - tooltipBoundingBox.bottom;

  let isBottomOutOfView = tooltipBottomOverflow < 0;
  let isRightOutOfView = tooltipRightOverflow < 0;
  
  // adding the overflow amount because its negative
  if (isBottomOutOfView) d3TooltipReference.style('top', offsetTop + tooltipBottomOverflow + 'px');
  if (isRightOutOfView) d3TooltipReference.style('left', offsetLeft + tooltipRightOverflow + 'px');
};

const handleShowDrillDownIcon = (event, nodeData, barWidthScale, openAllocationTable, theme, nameConventions) => {
    
  let targetX = event.target.x.baseVal.value;
  let targetY = event.target.y.baseVal.value;

  let parent = event.target.parentElement;
  let images = d3.select(parent).selectAll('image');
  
  if (!images.empty()) return;
  
  let offsetIndentation = (nodeData.depth > 0 ? indentation : 5);
  let barHeightForDepth = barHeight - (nodeData.depth * barHeightDecreaseStep);
  // background element to keep hover state
  d3.select(parent)
    .append('rect')
    .classed('iconPart', true)
    .attr('x', targetX - iconSize - offsetIndentation)
    .attr('y', targetY)
    .attr('height', iconSize + 10)
    .attr('width', iconSize + offsetIndentation + 5)
    .attr('fill', 'transparent');
  
  d3.select(parent)
    .append('image')
    .classed('iconPart', true)
    .on('mouseenter', (event, ) => { handleShowTooltip(event, 'drillDownIcon', nodeData, null, nameConventions);})
    .on('mouseleave', () => { d3.select('#tooltip').style('display', 'none');})
    .attr('href', theme ===DARK ? readMoreDark : readMore)
    .attr('x', targetX - iconSize - offsetIndentation - 5)
    .attr('y', targetY + (barHeightForDepth - iconSize) / 2)
    .attr('cursor', 'pointer')
    .attr('height', iconSize)
    .attr('width', iconSize)
    .on('click', () => openAllocationTable(nodeData));
};

const handleShowTooltip = (event, type, nodeData, toggleMode, nameConventions) => {
  if (type === 'labelTooltip' && !nodeData.showLabelTooltip) return;
  let tooltip = d3.select('#tooltip');
  tooltip._groups[0][0].anchorElement = event.target;
  tooltip._groups[0][0].distinct = nodeData.id;
  tooltip._groups[0][0].placement = type === 'drillDownIcon' ? 'top' :'right';
  tooltip.style('display', 'block');
  let content = getTooltipContent(type, nodeData, toggleMode, nameConventions);

  tooltip.html(content);
};

const getTooltipContent = (type, nodeData, toggleMode, nameConventions) => {
  switch (type) {
    case 'barTooltip':
      return `
          <div class="paddingXSmall">
            <p >${nodeData.stacklevel}</p>
            <hr class="lowOpacity">
            <p>${nodeData.path.split('---')[0] === 'Personnel' ? 'Number of FTE' : 'Items selected'}: <span class="tooltipText">${nodeData.numberofitems.toFixed(2)}<span></p>
            <hr class="lowOpacity">
            <p>Total ${toggleMode === TOGGLE_MODE.ALLOCATIONS ? nameConventions[PROGRAM].alias : 'item'} cost: <span class="tooltipText">${totalCostFormat(nodeData.total_cost)}<span></p>
          </div>
        `;
    case 'drillDownIcon':
      return `
            <p class="noMargin paddingXSmall">Show items</p>
  `;
    default:
      return `<p class="noMargin paddingXSmall">${nodeData.stacklevel}</p>`;
  }
  
};

const getValueForToggleMode = (programCost, allocationPercent, toggleMode) => {
  if (toggleMode === TOGGLE_MODE.ALLOCATIONS) return getPercentString(allocationPercent);
  else return totalCostFormat(programCost);
};

const getFillColor = (allocationPercent, toggleMode, isProgramNameLevel) => {
  if (toggleMode === TOGGLE_MODE.PROGRAM_COSTS) return blue;

  if (isProgramNameLevel) return gray;

  let percent = Math.round((allocationPercent + Number.EPSILON)*100)/100;
  return percent === 100 ? green : red;
};

const getBarWidth = (data, toggleMode) => {
  if (toggleMode === TOGGLE_MODE.ALLOCATIONS) return data.allocation_percent*0.95;
  
  return data.program_cost;
};

const resizeLabelText = (node, availableSpace) => {
  let labelLength = node.getComputedTextLength();
  if (labelLength > availableSpace) {
    let originalText = node.textContent;
    let labelNewLength = Math.round(originalText.length * 0.5);
    let newText = originalText.slice(0, labelNewLength).trim();
    newText += '...';
    node.textContent = newText;
    return true;
  }
  
  return false;
};

export {
  barMargin,
  barHeight,
  subBarMargin,
  centerText,
  indentation,
  translateDuration,
  appearDuration,
  barsDuration,
  iconSize,
  updateVerticalLines,
  getPercent,
  getPercentString,
  adjustTooltipPositionToFitViewport,
  handleShowDrillDownIcon,
  handleShowTooltip,
  getValueForToggleMode,
  getFillColor,
  TOGGLE_MODE,
  TOGGLE,
  getBarWidth,
  SORT_COLUMN_BY_TOGGLE_MODE,
  opacityValue,
  SEE_MORE_CHUNK_SIZE,
  textFontSize,
  barHeightDecreaseStep,
  fontSizeDecreaseStep,
  centerTextPercent,
  resizeLabelText
};
